import {AppActions, RootState} from 'app/rootReducer';
import Toast from 'components/Basic/Toast';
import {t} from 'i18next';
import {Epic} from 'redux-observable';
import {concat, from, Observable, of, tap} from 'rxjs';
import {
  catchError,
  concatMap,
  filter,
  ignoreElements,
  mergeMap,
  switchMap,
} from 'rxjs/operators';

import {
  refreshYouTubeToken,
  refreshYouTubeTokenFailure,
  refreshYouTubeTokenSuccess,
  signInYouTube,
  signInYouTubeFailure,
  signInYouTubeSuccess,
  signOutYouTube,
  signOutYouTubeFailure,
  signOutYouTubeSuccess,
  uploadToYoutube,
  uploadToYoutubeFailure,
  uploadToYoutubeSuccess,
} from './youtubeActions';
import {
  clearYouTubeAuth,
  doUploadToYoutube,
  loadYouTubeAuth,
  saveYouTubeAuth,
} from './youtubeUtils';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_YOUTUBE_CLIENT_ID;
const YOUTUBE_API_KEY = process.env.REACT_APP_YOUTUBE_API_KEY;

const noChannelError = {
  error: {
    message: 'User is not connected to a YouTube channel',
    errors: [{reason: 'youtubeSignupRequired'}],
  },
};

const useGoogleIdentityServices = (): Observable<any> => {
  return new Observable(subscriber => {
    if (!(window as any).google) {
      // Library should already be loaded from the script tags in the HTML head
      // If not loaded yet, wait a bit and check again
      const checkGoogleLoaded = setInterval(() => {
        if ((window as any).google) {
          clearInterval(checkGoogleLoaded);
          const google = (window as any).google;
          subscriber.next(google);
          subscriber.complete();
        }
      }, 100);

      // Set a timeout to avoid waiting forever
      setTimeout(() => {
        if (!(window as any).google) {
          clearInterval(checkGoogleLoaded);
          subscriber.error(
            new Error('Failed to load Google Identity Services'),
          );
        }
      }, 5000);
    } else {
      // Google Identity Services already loaded
      const google = (window as any).google;
      subscriber.next(google);
      subscriber.complete();
    }
  });
};

const waitForGapiInit = (): Observable<any> => {
  return new Observable(subscriber => {
    if (!(window as any).gapi) {
      const checkGapiLoaded = setInterval(() => {
        if ((window as any).gapiReady && (window as any).gapi) {
          clearInterval(checkGapiLoaded);
          const gapi = (window as any).gapi;

          gapi.load('client', () => {
            subscriber.next(gapi);
            subscriber.complete();
          });
        }
      }, 100);

      setTimeout(() => {
        if (!(window as any).gapi) {
          clearInterval(checkGapiLoaded);
          subscriber.error(new Error('Google API client not loaded'));
        }
      }, 8000);
    } else {
      const gapi = (window as any).gapi;
      gapi.load('client', () => {
        subscriber.next(gapi);
        subscriber.complete();
      });
    }
  });
};

const signInYouTubeEpic: Epic<AppActions, AppActions, RootState> = action$ =>
  action$.pipe(
    filter(signInYouTube.match),
    mergeMap(({payload}) =>
      useGoogleIdentityServices().pipe(
        mergeMap(google => {
          return waitForGapiInit().pipe(
            mergeMap(gapi => {
              return from(
                new Promise((resolve, reject) => {
                  try {
                    google.accounts.oauth2
                      .initTokenClient({
                        client_id: GOOGLE_CLIENT_ID,
                        scope:
                          'https://www.googleapis.com/auth/youtube.upload https://www.googleapis.com/auth/youtube',
                        callback: (tokenResponse: any) => {
                          if (tokenResponse.error) {
                            reject(new Error(tokenResponse.error));
                            return;
                          }

                          const accessToken = tokenResponse.access_token;

                          gapi.client.setApiKey(YOUTUBE_API_KEY);
                          gapi.client.load('youtube', 'v3').then(() => {
                            // @ts-ignore
                            gapi.client.youtube.channels
                              .list({
                                part: 'snippet',
                                mine: true,
                              })
                              .then(() => {
                                resolve({accessToken});
                              })
                              .catch(() => {
                                // If we can't get profile info, at least return the token
                                resolve({
                                  accessToken,
                                });
                              });
                          });
                        },
                      })
                      .requestAccessToken();
                  } catch (error) {
                    reject(error);
                  }
                }),
              );
            }),
          );
        }),
        mergeMap((authData: any) => {
          saveYouTubeAuth(authData);

          return of(signInYouTubeSuccess(authData)).pipe(
            tap(() => {
              if (payload?.onSuccess) {
                payload.onSuccess(authData);
              }
            }),
          );
        }),
        catchError((error: Error) =>
          concat(of(signInYouTubeFailure(error.message))),
        ),
      ),
    ),
  );

const signInYouTubeFailureEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(signInYouTubeFailure.match),
    tap(() => {
      Toast({
        type: 'warning',
        message: t('youtube.signInError', 'Failed to sign in with YouTube'),
      });
    }),
    ignoreElements(),
  );

const signOutYouTubeEpic: Epic<AppActions, AppActions, RootState> = action$ =>
  action$.pipe(
    filter(signOutYouTube.match),
    mergeMap(({payload}) =>
      useGoogleIdentityServices().pipe(
        mergeMap(google => {
          return from(
            new Promise<void>(resolve => {
              // Google Identity Services doesn't have a direct sign out method
              // We can revoke the token access
              if (google.gapi?.auth?.getToken()) {
                const token = google.gapi.auth.getToken();
                if (token) {
                  google.accounts.oauth2.revoke(token.access_token, () => {
                    resolve();
                  });
                } else {
                  resolve();
                }
              } else {
                resolve();
              }
            }),
          );
        }),
        mergeMap(() => {
          clearYouTubeAuth();

          return of(signOutYouTubeSuccess()).pipe(
            tap(() => {
              if (payload?.onSuccess) {
                payload.onSuccess();
              }
            }),
          );
        }),
        catchError((error: Error) =>
          concat(of(signOutYouTubeFailure(error.message))),
        ),
      ),
    ),
  );

const signOutYouTubeFailureEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(signOutYouTubeFailure.match),
    tap(() => {
      Toast({
        type: 'warning',
        message: t('youtube.signOutError', 'Failed to sign out from YouTube'),
      });
    }),
    ignoreElements(),
  );

const isTokenExpiredError = (error: any): boolean => {
  if (typeof error === 'string') {
    return (
      error.includes('invalid_token') ||
      error.includes('expired') ||
      error.includes('token_expired') ||
      error.includes('401')
    );
  }

  if (error && error.message) {
    return (
      error.message.includes('invalid_token') ||
      error.message.includes('expired') ||
      error.message.includes('token_expired') ||
      error.message.includes('401')
    );
  }

  return false;
};

const refreshToken = (): Observable<any> => {
  const currentAuth = loadYouTubeAuth();
  const tokenAge = currentAuth?.tokenTimestamp
    ? Date.now() - currentAuth.tokenTimestamp
    : Infinity;

  if (tokenAge < 45 * 60 * 1000) {
    return from(checkYouTubeChannelWithExistingToken()).pipe(
      mergeMap(authData => of(authData)),
      catchError(error => {
        if (isYouTubeChannelRequiredError(error)) {
          throw error;
        }
        if (isTokenExpiredError(error)) {
          return useGoogleIdentityServices().pipe(
            mergeMap(google => performTokenRefresh(google)),
          );
        }
        throw error;
      }),
    );
  }

  return useGoogleIdentityServices().pipe(
    mergeMap(google => performTokenRefresh(google)),
  );
};

const checkYouTubeChannelWithExistingToken = (): Promise<any> => {
  return new Promise((resolve, reject) => {
    const authData = loadYouTubeAuth();
    if (!authData || !authData.accessToken) {
      return reject(new Error('No authentication data available'));
    }

    const waitForGapi = () => {
      if (!(window as any).gapi || !(window as any).gapiReady) {
        setTimeout(waitForGapi, 100);
        return;
      }

      const gapi = (window as any).gapi;

      if (!gapi.client) {
        gapi.load('client', () => {
          checkChannelWithCurrentToken(gapi, authData, resolve, reject);
        });
      } else {
        checkChannelWithCurrentToken(gapi, authData, resolve, reject);
      }
    };

    waitForGapi();
  });
};

const checkChannelWithCurrentToken = (
  gapi: any,
  authData: any,
  resolve: Function,
  reject: Function,
) => {
  gapi.client.setApiKey(YOUTUBE_API_KEY);
  gapi.client.setToken({access_token: authData.accessToken});

  const loadYoutubeApi = () => {
    gapi.client.load('youtube', 'v3').then(() => {
      gapi.client.youtube.channels
        .list({
          part: 'snippet',
          mine: true,
        })
        .then((response: any) => {
          if (!response.result.items || response.result.items.length === 0) {
            reject(noChannelError);
          } else {
            resolve(authData);
          }
        })
        .catch((error: any) => {
          if (
            error?.result?.error?.errors?.some(
              (err: any) =>
                err.reason === 'youtubeSignupRequired' ||
                err.message?.includes('channel'),
            )
          ) {
            reject(noChannelError);
          } else if (isTokenExpiredError(error)) {
            reject(new Error('token_expired'));
          } else {
            reject(error);
          }
        });
    });
  };

  if (!gapi.client.youtube) {
    loadYoutubeApi();
  } else {
    gapi.client.youtube.channels
      .list({
        part: 'snippet',
        mine: true,
      })
      .then((response: any) => {
        if (!response.result.items || response.result.items.length === 0) {
          reject(noChannelError);
        } else {
          resolve(authData);
        }
      })
      .catch((error: any) => {
        if (
          error?.result?.error?.errors?.some(
            (err: any) =>
              err.reason === 'youtubeSignupRequired' ||
              err.message?.includes('channel'),
          )
        ) {
          reject(noChannelError);
        } else if (isTokenExpiredError(error)) {
          reject(new Error('token_expired'));
        } else {
          reject(error);
        }
      });
  }
};

const performTokenRefresh = (google: any): Observable<any> => {
  return waitForGapiInit().pipe(
    mergeMap(gapi => {
      return from(
        new Promise((resolve, reject) => {
          try {
            google.accounts.oauth2
              .initTokenClient({
                client_id: GOOGLE_CLIENT_ID,
                scope:
                  'https://www.googleapis.com/auth/youtube.upload https://www.googleapis.com/auth/youtube',
                prompt: '',
                callback: (tokenResponse: any) => {
                  if (tokenResponse.error) {
                    reject(new Error(tokenResponse.error));
                    return;
                  }

                  if (!tokenResponse.access_token) {
                    reject(new Error('No access token provided'));
                    return;
                  }

                  const accessToken = tokenResponse.access_token;

                  gapi.client.setApiKey(YOUTUBE_API_KEY);
                  gapi.client.load('youtube', 'v3').then(() => {
                    gapi.client.youtube.channels
                      .list({
                        part: 'snippet',
                        mine: true,
                      })
                      .then((response: any) => {
                        if (
                          !response.result.items ||
                          response.result.items.length === 0
                        ) {
                          reject(noChannelError);
                          return;
                        }

                        const authData = {accessToken};

                        saveYouTubeAuth(authData);
                        resolve(authData);
                      })
                      // @ts-ignore
                      .catch(error => {
                        if (
                          error?.result?.error?.errors &&
                          error.result.error.errors.some(
                            (err: any) =>
                              err.reason === 'youtubeSignupRequired' ||
                              err.message?.includes('channel'),
                          )
                        ) {
                          reject(noChannelError);
                          return;
                        }

                        // For other errors, just continue with token refresh
                        const authData = {accessToken};
                        saveYouTubeAuth(authData);
                        resolve(authData);
                      });
                  });
                },
              })
              .requestAccessToken();
          } catch (error) {
            reject(error);
          }
        }),
      );
    }),
  );
};

const refreshYouTubeTokenEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(refreshYouTubeToken.match),
    switchMap(({payload}) =>
      refreshToken().pipe(
        mergeMap((authData: any) => {
          if (payload?.onSuccess) {
            payload.onSuccess(authData);
          }
          return of(refreshYouTubeTokenSuccess(authData));
        }),
        catchError((error: any) => {
          let message = error.message || 'Failed to refresh YouTube token';

          if (isYouTubeChannelRequiredError(error)) {
            message =
              'You need to create a YouTube channel before uploading videos. Please visit YouTube to create your channel.';

            console.error(message, error);

            if (payload?.onError) {
              payload.onError('YouTube channel required');
            }

            return concat(of(refreshYouTubeTokenFailure(message)));
          }

          if (payload?.onError) {
            payload.onError(message);
          }

          return concat(of(refreshYouTubeTokenFailure(message)));
        }),
      ),
    ),
  );

const isYouTubeChannelRequiredError = (error: any): boolean => {
  if (Array.isArray(error?.error?.errors)) {
    return error.error.errors.some(
      (err: any) => err.reason === 'youtubeSignupRequired',
    );
  }

  if (Array.isArray(error?.result?.error?.errors)) {
    return error.result.error.errors.some(
      (err: any) => err.reason === 'youtubeSignupRequired',
    );
  }

  return false;
};

export const uploadToYoutubeEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(uploadToYoutube.match),
    concatMap(({payload}) =>
      from(doUploadToYoutube(payload.audio)).pipe(
        concatMap(response => {
          Toast({
            type: 'success',
            message: 'Successfully uploaded to YouTube!',
          });

          if (payload?.onSuccess) {
            payload.onSuccess();
          }

          return of(uploadToYoutubeSuccess(response));
        }),
        catchError((error: any) => {
          const missingChannel =
            'You need to create a YouTube channel before uploading videos. Please visit YouTube to create your channel.';
          if (isYouTubeChannelRequiredError(error)) {
            Toast({
              type: 'error',
              message: missingChannel,
            });

            console.error(missingChannel, error);

            if (payload?.onError) {
              payload.onError(missingChannel);
            }

            return of(
              uploadToYoutubeFailure({
                channelRequired: true,
                message: missingChannel,
              }),
            );
          }

          if (isTokenExpiredError(error)) {
            return refreshToken().pipe(
              // After token refresh, retry the upload
              switchMap(_ => {
                return from(doUploadToYoutube(payload.audio)).pipe(
                  mergeMap(response => {
                    Toast({
                      type: 'success',
                      message: 'Successfully uploaded to YouTube!',
                    });

                    if (payload?.onSuccess) {
                      payload.onSuccess();
                    }

                    return of(uploadToYoutubeSuccess(response));
                  }),
                  catchError(retryError => {
                    if (isYouTubeChannelRequiredError(retryError)) {
                      Toast({
                        type: 'error',
                        message: missingChannel,
                      });

                      if (payload?.onError) {
                        payload.onError(missingChannel);
                      }

                      return of(
                        uploadToYoutubeFailure({
                          channelRequired: true,
                          message: missingChannel,
                        }),
                      );
                    }

                    const errorMessage =
                      typeof retryError === 'string'
                        ? retryError
                        : 'Failed to upload to YouTube even after token refresh';

                    Toast({
                      type: 'error',
                      message: errorMessage,
                    });

                    return of(uploadToYoutubeFailure(retryError));
                  }),
                );
              }),
              catchError(refreshError => {
                // If token refresh fails, show auth error
                Toast({
                  type: 'error',
                  message:
                    'YouTube authentication expired. Please sign in again.',
                });

                return of(uploadToYoutubeFailure(refreshError));
              }),
            );
          }

          // For other errors, handle normally
          const errorMessage =
            typeof error === 'string' ? error : 'Failed to upload to YouTube';

          Toast({
            type: 'error',
            message: errorMessage,
          });

          return of(uploadToYoutubeFailure(error));
        }),
      ),
    ),
  );

export const youtubeEpics = [
  signInYouTubeEpic,
  signInYouTubeFailureEpic,
  signOutYouTubeEpic,
  signOutYouTubeFailureEpic,
  uploadToYoutubeEpic,
  refreshYouTubeTokenEpic,
].flatMap(epic => epic);
